import React, { useEffect, useState, useCallback } from 'react';
import api from '@/utils/api';
import { Package, Search, Eye, DollarSign, Clock, Truck, CheckCircle, XCircle, Filter, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Store, Lock, PackageCheck, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';

// ImpactGolf is the fulfillment source tenant - it physically ships every
// fulfillment order regardless of which tenant's storefront it was placed
// on, so its Order Management view is widened server-side to include those
// orders too. Matches FULFILLMENT_SOURCE_TENANT_ID in payout_service.py.
const IMPACT_GOLF_TENANT_ID = 'tenant_2a7782078b27';

const CARRIERS = ['UPS', 'FedEx', 'USPS', 'DHL', 'Other'];

// Internal-only configuration keys that admins shouldn't see in the order details
// (raw IDs, server bookkeeping, leftover client modifiers — server now ignores these
// for pricing but they still get persisted on the cart row for traceability).
const HIDDEN_CONFIG_KEYS = new Set([
  'purchase_mode',
  'shaft_price',
  'price_modifier',
  'iron_total_base',
  'iron_per_price',
  'iron_club_count',
  'shaft_product_id',
  'iron_clubs',
  'ironClubs',
]);

const FRIENDLY_LABELS = {
  shaft_flex: 'Flex',
  shaft_flex_name: 'Flex',
  shaft_name: 'Shaft',
  grip: 'Grip',
  grip_name: 'Grip',
  grip_wraps: 'Grip Wraps',
  grip_logo: 'Logo Position',
  adapter_type: 'Adapter',
  adapter_type_name: 'Adapter',
  playing_length: 'Length',
  playing_length_name: 'Length',
  tipping: 'Tipping',
  pured: 'Pured',
  dexterity: 'Dexterity',
  dexterity_value: 'Dexterity',
};

const prettyLabel = (key) =>
  FRIENDLY_LABELS[key] ||
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatConfigValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    return value.name || value.value || value.id || JSON.stringify(value);
  }
  return String(value);
};

/**
 * Flatten a cart-item configuration into [{key, label, value}] rows safe for
 * direct rendering. Handles nested variant_selections and skips internal keys
 * so React never receives a raw object as a child.
 */
const renderConfigEntries = (configuration) => {
  if (!configuration || typeof configuration !== 'object') return [];
  const rows = [];

  for (const [key, value] of Object.entries(configuration)) {
    if (HIDDEN_CONFIG_KEYS.has(key)) continue;
    if (value == null || value === '') continue;

    if (key === 'variant_selections' && typeof value === 'object' && !Array.isArray(value)) {
      for (const [groupKey, sel] of Object.entries(value)) {
        const formatted = formatConfigValue(sel);
        if (formatted) {
          rows.push({
            key: `variant-${groupKey}`,
            label: prettyLabel(groupKey),
            value: formatted,
          });
        }
      }
      continue;
    }

    // Skip the *_name twins when their non-name counterpart is also present
    // (e.g. shaft_flex='stiff', shaft_flex_name='Stiff') — prefer the human name.
    if (!key.endsWith('_name')) {
      const nameTwin = `${key}_name`;
      if (configuration[nameTwin]) continue;
    }

    rows.push({
      key,
      label: prettyLabel(key),
      value: formatConfigValue(value),
    });
  }

  return rows;
};

export default function AdminOrders() {
  const { tenant } = useTenant();
  const isImpactGolf = tenant?.tenant_id === IMPACT_GOLF_TENANT_ID;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortValue, setSortValue] = useState('created_at:desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackingStatus, setTrackingStatus] = useState(null);
  const [trackingNumberInput, setTrackingNumberInput] = useState('');
  const [trackingCarrierInput, setTrackingCarrierInput] = useState('');

  const limit = 20;
  const [sortBy, sortOrder] = sortValue.split(':');

  // Debounce free-text search input -> searchQuery (300ms)
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to page 1 whenever a filter (other than search, handled above) changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter, paymentFilter, dateFrom, dateTo, sortValue]);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (paymentFilter !== 'all') params.payment_status = paymentFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await api.get('/admin/orders', { params });
      setOrders(response.data.orders || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalOrders(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortOrder, searchQuery, statusFilter, paymentFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/orders/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load order stats');
    }
  };

  const handleUpdateStatus = async (orderId, newStatus, extra = {}) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus, ...extra });
      toast.success('Order status updated');
      loadOrders();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Delete order #${order.order_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${order.order_id}`);
      toast.success('Order deleted');
      loadOrders();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const openTrackingModal = (order, status) => {
    setTrackingOrder(order);
    setTrackingStatus(status);
    setTrackingNumberInput(order.tracking_number || '');
    setTrackingCarrierInput(order.carrier || '');
  };

  const handleStatusSelect = (order, newStatus) => {
    // Prompt for tracking only when transitioning into Shipped/Delivered
    // for the first time - once a tracking number is already on file,
    // just update the status directly (editing tracking is a separate action).
    if ((newStatus === 'shipped' || newStatus === 'delivered') && !order.tracking_number) {
      openTrackingModal(order, newStatus);
    } else {
      handleUpdateStatus(order.order_id, newStatus);
    }
  };

  const confirmTracking = async () => {
    if (!trackingOrder) return;
    await handleUpdateStatus(trackingOrder.order_id, trackingStatus, {
      tracking_number: trackingNumberInput.trim() || undefined,
      carrier: trackingCarrierInput || undefined,
    });
    setTrackingOrder(null);
  };

  const skipTracking = async () => {
    if (!trackingOrder) return;
    await handleUpdateStatus(trackingOrder.order_id, trackingStatus);
    setTrackingOrder(null);
  };

  const handleViewDetails = async (order) => {
    try {
      const response = await api.get(`/admin/orders/${order.order_id}`);
      setSelectedOrder(response.data);
      setDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    setPaymentFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortValue('created_at:desc');
  };

  const hasActiveFilters = searchInput || statusFilter !== 'all' || paymentFilter !== 'all' || dateFrom || dateTo || sortValue !== 'created_at:desc';

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'processing': return <Package className="h-4 w-4" />;
      case 'shipped': return <Truck className="h-4 w-4" />;
      case 'delivered': return <CheckCircle className="h-4 w-4" />;
      default: return <Package className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'processing': return 'bg-blue-100 text-blue-700';
      case 'shipped': return 'bg-purple-100 text-purple-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'refunded': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="text-center py-12" data-testid="orders-loading">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading orders...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-orders-page">
      <h2 className="text-2xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Chivo, sans-serif' }}>
        Order Management
      </h2>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_orders}</p>
              </div>
              <Package className="h-8 w-8 text-[#334155]" />
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Revenue</p>
                <p className="text-2xl font-bold text-[#4A6741]">${stats.total_revenue?.toFixed(2)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-[#4A6741]" />
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending_orders}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Processing</p>
                <p className="text-2xl font-bold text-blue-600">{stats.processing_orders}</p>
              </div>
              <Package className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Last 30 Days</p>
                <p className="text-2xl font-bold text-gray-900">{stats.recent_orders_30d}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-[#4A6741]" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by order ID, email, or customer name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10 bg-gray-50 border-[#F3F4F6]"
            data-testid="order-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] bg-gray-50 border-[#F3F4F6]" data-testid="status-filter-select">
            <SelectValue placeholder="Order Status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="w-[160px] bg-gray-50 border-[#F3F4F6]" data-testid="payment-filter-select">
            <SelectValue placeholder="Payment Status" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Payments</SelectItem>
            <SelectItem value="completed">Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Secondary row: date range + sort + clear */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 md:items-center">
        <div className="flex items-center gap-2">
          <label htmlFor="order-date-from" className="text-xs text-gray-500 whitespace-nowrap">From</label>
          <Input
            id="order-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-[150px] bg-gray-50 border-[#F3F4F6]"
            data-testid="order-date-from-input"
          />
          <label htmlFor="order-date-to" className="text-xs text-gray-500 whitespace-nowrap">To</label>
          <Input
            id="order-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-[150px] bg-gray-50 border-[#F3F4F6]"
            data-testid="order-date-to-input"
          />
        </div>
        <Select value={sortValue} onValueChange={setSortValue}>
          <SelectTrigger className="w-[180px] bg-gray-50 border-[#F3F4F6]" data-testid="order-sort-select">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="created_at:desc">Newest First</SelectItem>
            <SelectItem value="created_at:asc">Oldest First</SelectItem>
            <SelectItem value="total:desc">Highest Total</SelectItem>
            <SelectItem value="total:asc">Lowest Total</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearFilters}
            className="border-[#F3F4F6]"
            data-testid="clear-filters-btn"
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Clear Filters
          </Button>
        )}
        <p className="text-sm text-gray-500 md:ml-auto" data-testid="orders-result-count">
          {totalOrders} order{totalOrders === 1 ? '' : 's'} found
        </p>
      </div>

      {/* Orders List */}
      <div className="space-y-4" data-testid="orders-list">
        {loading ? (
          <div className="text-center py-12" data-testid="orders-loading-inline">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-gray-50 p-8 rounded-lg border border-[#F3F4F6] text-center text-gray-500" data-testid="orders-empty-state">
            No orders found
          </div>
        ) : (
          orders.map((order) => (
            <div 
              key={order.order_id} 
              className="bg-gray-50 rounded-lg border border-[#F3F4F6] overflow-hidden"
              data-testid={`order-card-${order.order_id}`}
            >
              {/* Order Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
              >
                <div className="flex items-center space-x-4">
                  <div>
                    <p className="text-xs text-[#334155] font-bold uppercase tracking-wider">
                      Order #{order.order_id}
                    </p>
                    <p className="text-gray-600 text-sm">{order.user_email}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {order.is_guest && (
                        <span className="inline-block text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                          Guest
                        </span>
                      )}
                      {isImpactGolf && order.store_name && (
                        <span
                          className="inline-flex items-center gap-1 text-xs bg-[#4A6741]/10 text-[#4A6741] px-2 py-0.5 rounded font-medium"
                          data-testid={`order-store-${order.order_id}`}
                        >
                          <Store className="h-3 w-3" />
                          {order.store_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">${order.total?.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      <span className="ml-1 capitalize">{order.status}</span>
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${getPaymentStatusColor(order.payment_status)}`}>
                      {order.payment_status === 'completed' ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                      <span className="capitalize">{order.payment_status === 'completed' ? 'Paid' : order.payment_status}</span>
                    </span>
                  </div>
                  
                  {!(order.is_fulfillment_order && !isImpactGolf) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order); }}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete order"
                      data-testid={`delete-order-${order.order_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  
                  {expandedOrder === order.order_id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Expanded Order Details */}
              {expandedOrder === order.order_id && (
                <div className="border-t border-[#F3F4F6] p-4 bg-white">
                  {/* Order Items */}
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-gray-700 mb-2">Items</h4>
                    <div className="space-y-2">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center space-x-3">
                            {item.product_image && (
                              <img src={item.product_image} alt={item.product_name} className="w-10 h-10 rounded object-cover" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                              <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                            </div>
                          </div>
                          <p className="text-sm font-bold text-gray-900">${item.total_price?.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Order Info & Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-[#F3F4F6]">
                    <div className="flex items-center space-x-4">
                      <div>
                        <p className="text-xs text-gray-500">Payment Method</p>
                        <p className="text-sm font-medium capitalize">{order.payment_method}</p>
                      </div>
                      {order.shipping_address && (
                        <div>
                          <p className="text-xs text-gray-500">Shipping</p>
                          <p className="text-sm font-medium">
                            {order.shipping_address.city}, {order.shipping_address.state}
                          </p>
                        </div>
                      )}
                      {order.tracking_number && (
                        <div data-testid={`tracking-info-${order.order_id}`}>
                          <p className="text-xs text-gray-500">Tracking</p>
                          <p className="text-sm font-medium flex items-center gap-1">
                            <PackageCheck className="h-3.5 w-3.5 text-[#4A6741]" />
                            {order.carrier ? `${order.carrier} - ` : ''}{order.tracking_number}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {order.is_fulfillment_order && !isImpactGolf ? (
                        <div
                          className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-[#F3F4F6] rounded px-3 py-2"
                          title="This order is fulfilled by ImpactGolf Center - only they can update its status"
                          data-testid={`status-readonly-${order.order_id}`}
                        >
                          <Lock className="h-3.5 w-3.5" />
                          <span className="capitalize">{order.status}</span>
                        </div>
                      ) : (
                        <>
                          <Select
                            value={order.status}
                            onValueChange={(value) => handleStatusSelect(order, value)}
                          >
                            <SelectTrigger className="w-[150px] bg-white border-[#F3F4F6]" data-testid={`status-select-${order.order_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="processing">Processing</SelectItem>
                              <SelectItem value="shipped">Shipped</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                              <SelectItem value="refunded">Refunded</SelectItem>
                            </SelectContent>
                          </Select>
                          {(order.status === 'shipped' || order.status === 'delivered') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openTrackingModal(order, order.status)}
                              data-testid={`edit-tracking-${order.order_id}`}
                            >
                              <Pencil className="h-3.5 w-3.5 mr-1" />
                              {order.tracking_number ? 'Edit Tracking' : 'Add Tracking'}
                            </Button>
                          )}
                        </>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(order)}
                        data-testid={`view-details-${order.order_id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Full Details
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && orders.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between mt-6" data-testid="orders-pagination">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="orders-prev-page-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="orders-next-page-btn"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Order ID</p>
                  <p className="font-mono text-sm">{selectedOrder.order_id}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Date</p>
                  <p className="text-sm">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
                {isImpactGolf && selectedOrder.store_name && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Store</p>
                    <p className="text-sm">{selectedOrder.store_name}</p>
                  </div>
                )}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Customer</p>
                  <p className="text-sm">{selectedOrder.user_email}</p>
                  {(selectedOrder.user_info?.name || selectedOrder.guest_info) && (
                    <p className="text-xs text-gray-600">
                      {selectedOrder.user_info?.name ||
                        `${selectedOrder.guest_info?.first_name || ''} ${selectedOrder.guest_info?.last_name || ''}`.trim()}
                      {selectedOrder.is_guest && ' (Guest)'}
                    </p>
                  )}
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Payment</p>
                  <p className="text-sm capitalize">{selectedOrder.payment_method}</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold mt-1 ${getPaymentStatusColor(selectedOrder.payment_status)}`}>
                    {selectedOrder.payment_status === 'completed' ? 'Paid' : selectedOrder.payment_status}
                  </span>
                </div>
              </div>
              
              {selectedOrder.shipping_address && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-2">Shipping Address</p>
                  <p className="text-sm">
                    {selectedOrder.shipping_address.address}<br />
                    {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.zip}<br />
                    {selectedOrder.shipping_address.country}
                  </p>
                </div>
              )}
              
              <div>
                <p className="text-sm font-bold text-gray-700 mb-2">Order Items</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.product_name}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity} x ${item.base_price?.toFixed(2)}</p>
                        {item.configuration && Object.keys(item.configuration).length > 0 && (
                          <div className="mt-2 text-xs text-gray-600">
                            {renderConfigEntries(item.configuration).map(({ key, label, value }) => (
                              <span key={key} className="inline-block mr-2 mb-1 px-2 py-0.5 bg-gray-200 rounded">
                                {label}: {value}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="font-bold text-gray-900">${item.total_price?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t">
                <p className="text-lg font-bold">Total</p>
                <p className="text-2xl font-bold text-[#4A6741]">${selectedOrder.total?.toFixed(2)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Shipment Tracking Modal */}
      <Dialog open={!!trackingOrder} onOpenChange={(open) => !open && setTrackingOrder(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="tracking-modal">
          <DialogHeader>
            <DialogTitle>
              {trackingOrder?.tracking_number ? 'Edit Shipment Tracking' : 'Add Shipment Tracking'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-500">
              Order #{trackingOrder?.order_id} — the customer will be emailed automatically once you save a tracking number.
            </p>
            <div>
              <Label htmlFor="tracking-number">Tracking Number</Label>
              <Input
                id="tracking-number"
                value={trackingNumberInput}
                onChange={(e) => setTrackingNumberInput(e.target.value)}
                placeholder="e.g. 1Z999AA10123456784"
                className="mt-1"
                data-testid="tracking-number-input"
              />
            </div>
            <div>
              <Label htmlFor="tracking-carrier">Carrier (optional)</Label>
              <Select value={trackingCarrierInput} onValueChange={setTrackingCarrierInput}>
                <SelectTrigger id="tracking-carrier" className="mt-1 bg-white" data-testid="tracking-carrier-select">
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CARRIERS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              {trackingOrder?.tracking_number ? (
                <Button variant="outline" size="sm" onClick={() => setTrackingOrder(null)} data-testid="cancel-tracking-btn">
                  Cancel
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={skipTracking} data-testid="skip-tracking-btn">
                  Skip
                </Button>
              )}
              <Button
                size="sm"
                className="bg-[#4A6741] hover:bg-[#3d5636] text-white"
                onClick={confirmTracking}
                disabled={!trackingNumberInput.trim()}
                data-testid="save-tracking-btn"
              >
                Save &amp; Notify Customer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
